import {
  type CONTENT_TYPES,
  type PerContentListProps,
} from '@hivebrite/design-system/ContentList';

import getDateStr from 'hb-react/shared/components/EventDate/utils/getDateStr';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import { AVAILABLE_CONTENT_TYPES } from 'hb-react/shared/utils/novaContentData/constants';
import {
  type NovaContents,
  type AvailableContentTypes,
  type NetworkEvent,
  type UserEvent,
} from 'hb-react/shared/utils/novaContentData/types';
import { type TimeZoneConfiguration } from 'hb-react/types/timeZoneConfiguration';

const defaultDisplayFields = {
  content: true,
  contentType: true,
  group: true,
};

interface SerializeNovaDataOptions {
  displayFields?: PerContentListProps[CONTENT_TYPES]['displayFields'];
  timeZoneConfiguration: TimeZoneConfiguration;
}

export const serializeForContentList = (
  item: NovaContents[AvailableContentTypes],
  {
    displayFields = defaultDisplayFields,
    timeZoneConfiguration,
  }: SerializeNovaDataOptions,
): PerContentListProps[CONTENT_TYPES] | undefined => {
  const baseProps = {
    displayFields,
  };

  switch (item.type) {
    case AVAILABLE_CONTENT_TYPES.NEWS:
      return {
        ...baseProps,
        contentType: 'news',
        content: item.sanitizedContent,
        id: item.id,
        image: item.coverPicture?.mediumUrl,
        title: item.subject,
      } as PerContentListProps[CONTENT_TYPES.NEWS];

    case AVAILABLE_CONTENT_TYPES.FORUM:
      return {
        ...baseProps,
        contentType: AVAILABLE_CONTENT_TYPES.FORUM,
        author: {
          id: item.user.id,
          name: item.user.name,
          photoThumbUrl: item.user.photo.thumbnailUrl,
        },
        content: item.sanitizedContent,
        displayFields: {
          ...baseProps.displayFields,
          author: true,
        },
        id: item.id,
        imageSrc: item.coverPicture?.mediumUrl,
        name: item.title,
      } as PerContentListProps[CONTENT_TYPES.FORUM];

    case AVAILABLE_CONTENT_TYPES.USER_EVENT:
      return {
        ...baseProps,
        ...serializeCommonEventData(item, baseProps, timeZoneConfiguration),
        image: item.logo?.mediumUrl,
      } as PerContentListProps[CONTENT_TYPES.EVENT];

    case AVAILABLE_CONTENT_TYPES.NETWORK_EVENT:
      return {
        ...baseProps,
        ...serializeCommonEventData(item, baseProps, timeZoneConfiguration),
        onlineEvent: Boolean(item.webinarUrl || item.isInternalZoomMeeting),
        image: item.coverPicture?.mediumUrl,
      } as PerContentListProps[CONTENT_TYPES.EVENT];

    default:
      // @ts-expect-error typescript consider `item` as `never` since all known types are handled above
      console.warn(`Unsupported content: ${item.type}`);

      return undefined;
  }
};

const serializeCommonEventData = (
  item: NetworkEvent | UserEvent,
  baseProps,
  timeZoneConfiguration: TimeZoneConfiguration = {},
) => {
  const { currentUserTimezone, eventDatesInUserTimezone } =
    timeZoneConfiguration;
  const timezone = eventDatesInUserTimezone
    ? (currentUserTimezone as string | undefined)
    : item.timezone;

  return {
    contentType: 'event',
    date: getDateStr({
      startDate: item.startDate,
      // We don't want to display the endDate in the content recommendation list
      endDate: undefined,
      timezone,
    }),
    displayFields: {
      ...baseProps.displayFields,
      location: true,
      date: true,
      group: true,
    },
    id: item.id,
    location: item?.location?.location,
    formattedDate: {
      day: dayjs(item.startDate).tz(timezone).format('DD'),
      month: dayjs(item.startDate).tz(timezone).format('MM'),
    },
    name: item.title,
    // Attendees are expected in monorepo, but not needed in content reco list
    // displayFields.attendees is false by default
    attendees: [],
    totalAttendees: 0,
  };
};
